import { client } from '@/client';
import {
  HubspotErrorLogsRequest,
  HubspotErrorLogsResponse,
  HubspotErrorLogSummary,
} from '@/client/hubspot';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';

export const HUBSPOT_ERROR_LOGS_QUERY_KEY = 'hubspotErrorLogs';
export const HUBSPOT_ERROR_SUMMARY_QUERY_KEY = 'hubspotErrorSummary';

const FIVE_MINUTES = 5 * 60 * 1000;

export const useHubspotErrorLogs = (
  params: HubspotErrorLogsRequest,
): UseQueryResult<HubspotErrorLogsResponse> & {
  errorLogs: HubspotErrorLogsResponse | undefined;
} => {
  const { data, ...rest } = useQuery(
    [HUBSPOT_ERROR_LOGS_QUERY_KEY, params],
    () => client.hubspot.getErrorLogs(params),
    { keepPreviousData: true },
  );

  return {
    errorLogs: data,
    ...(rest as UseQueryResult<HubspotErrorLogsResponse>),
  };
};

export const useHubspotErrorSummary = (
  enabled: boolean,
): UseQueryResult<HubspotErrorLogSummary> & {
  summary: HubspotErrorLogSummary | undefined;
} => {
  const { data, ...rest } = useQuery(
    [HUBSPOT_ERROR_SUMMARY_QUERY_KEY],
    () => client.hubspot.getErrorLogSummary(),
    {
      enabled,
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      staleTime: 30000,
    },
  );

  return { summary: data, ...(rest as UseQueryResult<HubspotErrorLogSummary>) };
};

export const useDeleteHubspotErrorLog = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation(
    'deleteHubspotErrorLog',
    (id: string) => client.hubspot.deleteErrorLog(id),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(HUBSPOT_ERROR_LOGS_QUERY_KEY),
          queryClient.invalidateQueries(HUBSPOT_ERROR_SUMMARY_QUERY_KEY),
        ]);
      },
    },
  );

  return { deleteErrorLog: mutateAsync, ...rest };
};
